'use client';

import React from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { Answer, SurveyQuestion, NormalQuestion } from '~/types/job-detail';

interface CompanyInterviewProps {
  answers: Answer[];
  surveyQuestions?: SurveyQuestion[];
  normalQuestions?: NormalQuestion[];
  interviewComment?: string;
  isMobile: boolean;
}

const br = (text = '') => text.replace(/\r\n|\r|\n/g, '<br>');

export const CompanyInterview: React.FC<CompanyInterviewProps> = ({
  answers,
  surveyQuestions = [],
  normalQuestions = [],
  interviewComment,
  isMobile,
}) => {
  const hasContent =
    Object.keys(answers || {}).length > 0 || !!interviewComment;

  if (!hasContent) return null;

  const mobile = isMobile;
  const pc = !mobile;

  return (
    <div className="u-flex -column">
      {/* ===== 見出し ===== */}
      <div className="p-job-detail__information-table-row">
        {mobile ? (
          <div className="p-job-detail__information-table-header">
            <h2
              id="workplace_environment"
              className={classNames(
                'p-job-detail__heading',
                'u-flex',
                'u-font-bold-imp',
                '-items-center',
                'u-pl6',
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_chat_bubbles_white.svg"
                alt="chat bubbles white"
                width={16}
                height={16}
                className="u-mr4"
                style={{ maxWidth: 'none', height: '16px' }}
              />
              職場環境について
            </h2>
          </div>
        ) : (
          <div className="p-job-detail__information-table-header u-flex">
            <Image
              src="/nextjs/images/icons/ic_chat_bubbles_green.svg"
              alt="chat bubbles green"
              width={20}
              height={20}
              className="u-mr6 u-mt2"
              style={{ maxWidth: 'none', height: '20px' }}
            />
            <h2 id="workplace_environment" className="p-job-detail__heading">
              職場環境について
            </h2>
          </div>
        )}
        <div className="p-job-detail__information-table-content" />
      </div>

      {/* ===== アンケート ===== */}
      {surveyQuestions.length > 0 && (
        <div className="p-job-detail__survey u-mb24">
          {surveyQuestions.map((q) => {
            const answer = answers[q.id];

            /* ラッパーは置かない ── React.Fragment で 3 要素を直接返す */
            return (
              <React.Fragment key={q.id}>
                {/* 左のテキスト */}
                <div className="p-job-detail__survey-text-start">
                  {q.question_min}
                </div>

                {/* メーター */}
                <div className="p-job-detail__survey-meter">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span
                      key={i}
                      className={classNames('p-job-detail__survey-meter-item', {
                        '-active': answer?.answer_value === i,
                      })}
                    />
                  ))}
                </div>

                {/* 右のテキスト */}
                <div className="p-job-detail__survey-text">
                  {q.question_max}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* ===== 取材 Q&A ===== */}
      {normalQuestions.length > 0 && (
        <div className="p-job-detail__interview-qa u-mb24">
          <h3 className="p-job-detail__interview-title">
            建職バンクの取材レポート
          </h3>

          <div className="p-job-detail__interview-qa-list">
            {normalQuestions.map((q) => {
              const answer = answers[q.id];
              return (
                <div key={q.id} className="u-flex u-gap8 -column u-mb12">
                  <p className="p-job-detail__interview-qa-question">
                    Q.&nbsp;{q.question_text}
                  </p>
                  <div
                    className="p-job-detail__interview-qa-answer"
                    dangerouslySetInnerHTML={{
                      __html: br(answer?.answer_text || ''),
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== 編集部コメント ===== */}
      {interviewComment && (
        <div className="p-job-detail__interview-comment">
          <h3 className="p-job-detail__interview-comment-title">
            <Image
              src="/nextjs/images/icons/ic_mic.svg"
              alt="mic"
              width={20}
              height={20}
              className="p-job-detail__interview-comment-icon"
              style={{ maxWidth: 'none', height: '20px' }}
            />
            <span className="u-ml4">建職バンク編集部コメント</span>
          </h3>
          <p
            className="p-job-detail__interview-comment-body"
            dangerouslySetInnerHTML={{ __html: br(interviewComment) }}
          />
        </div>
      )}
    </div>
  );
};

export default CompanyInterview;
